import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import FooterNavigation from 'src/components/pages/create-campaign/FooterNavigation';
import ProgressStepsMobile from 'src/components/pages/account-setup/ProgressStepsMobile';
import AccountSetupIntro from 'src/components/pages/account-setup/AccountSetupIntro';
import AccountSetupInformation from 'src/components/pages/account-setup/AccountSetupInformation';
import ConnectFacebookToInstagram from 'src/components/pages/account-setup/ConnectFacebookToInstagram';
import ChooseBusinessAccount from 'src/components/pages/account-setup/ChooseBusinessAccount';
import CreateAdAccount from 'src/components/pages/account-setup/CreateAdAccount';
import CreatePixel from 'src/components/pages/account-setup/CreatePixel';
import { AuthContext } from 'src/contexts';
import { useHttp } from 'src/hooks';
import IconSvg from 'src/components/common/ui/IconSvg';
import { Dialog } from '@headlessui/react';

export default function AccountSetup() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBusiness, setSelectedBusiness] = useState<string | null>(null);
  const [selectedAdAccount, setSelectedAdAccount] = useState<string | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [connected, setConnected] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const navigate = useNavigate();
  const { profile, accessToken, fetchProfile } = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const totalSteps = 6;
  const stepFromUrl = Number(searchParams.get('step')) || 1;
  const [currentStep, setCurrentStep] = useState(stepFromUrl);

  // Check if Facebook is connected and if setup was completed
  const isFacebookConnected = !!profile?.facebook?.id;

  const handleStepChange = (step: number) => {
    setCurrentStep(step);
    setSearchParams({ step: String(step) });
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      handleStepChange(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      handleStepChange(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    sendRequest(
      {
        url: `user/mark-setup-completed`,
        accessToken,
        method: 'POST',
      },
      data => {
        if (data) {
          fetchProfile();
          navigate('/dashboard/create-campaign');
        }
      },
    );
  };

  const handleRemoveAccount = () => {
    setIsOpen(true);
  };

  const confirmDisconnect = async () => {
    setIsDisconnecting(true);
    sendRequest(
      {
        url: `facebook/disconnect`,
        accessToken,
        method: 'POST',
      },
      data => {
        setIsDisconnecting(false);
        setIsOpen(false);
        if (data) {
          fetchProfile();
          setCurrentStep(1);
        }
      },
    );
  };

  if (isFacebookConnected && profile?.hasCompletedSetup) {
    return (
      <>
        {/* Disconnect Confirmation Modal */}
        <Dialog open={isOpen} onClose={() => setIsOpen(false)} className="relative z-50">
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

          {/* Modal container */}
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-gray-800">
                Disconnect Facebook Account
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-gray-600">
                Are you sure you want to disconnect your Facebook account? This will remove all
                associated data and you&apos;ll need to reconnect to use Facebook features.
              </Dialog.Description>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isDisconnecting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDisconnect}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  disabled={isDisconnecting}
                >
                  {isDisconnecting ? (
                    <span className="flex items-center justify-center">
                      <IconSvg icon="spinner" className="animate-spin h-4 w-4 mr-2" />
                      Disconnecting...
                    </span>
                  ) : (
                    'Disconnect'
                  )}
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>

        {/* Account Connected Screen */}
        <div className="my-8 p-6 border border-[#E5E7E7] rounded-2xl">
          <div className="flex flex-col items-center justify-center min-h-[70vh] p-4">
            <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Header with Icon */}
              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-3">
                  <IconSvg icon="connected" className="h-6 w-6 text-green-600" />
                </div>
                <h2 className="text-lg font-semibold text-gray-800">Account Connected</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Your Facebook account is successfully connected
                </p>
              </div>

              {/* Profile Section */}
              <div className="flex items-center mb-6 p-3 bg-gray-50 rounded-lg border border-gray-100">
                <img
                  src={profile.facebook.profilePicture || '/default-profile.png'}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover border border-gray-200"
                />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">{profile.facebook.name}</h3>
                  <p className="text-xs text-gray-500">Facebook Business Account</p>
                </div>
              </div>

              {/* Status Indicators */}
              <div className="flex justify-between mb-6 px-2">
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">Connection Status</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">Setup Status</p>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Completed
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    navigate('/dashboard/create-campaign');
                  }}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Proceed to Campaign Creation
                  <IconSvg icon="proceed" className="ml-2 -mr-1 w-4 h-4" />
                </button>
                <button
                  onClick={handleRemoveAccount}
                  className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Disconnect Account
                </button>
              </div>

              {/* Footer */}
              <div className="mt-5 pt-4 border-t border-gray-100 text-center">
                <p className="text-xs text-gray-500">
                  Last connected:{' '}
                  {new Date(profile.facebook.generatedAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ProgressStepsMobile currentStep={currentStep} />
      {currentStep === 1 && <AccountSetupIntro setIsStepValid={setIsStepValid} />}
      {currentStep === 2 && <AccountSetupInformation setIsStepValid={setIsStepValid} />}
      {currentStep === 3 && <ConnectFacebookToInstagram onConnectionChange={setConnected} />}
      {currentStep === 4 && (
        <ChooseBusinessAccount
          selectedBusiness={selectedBusiness}
          onSelectBusiness={setSelectedBusiness}
          setIsStepValid={setIsStepValid}
        />
      )}
      {currentStep === 5 && (
        <CreateAdAccount
          businessId={selectedBusiness}
          selectedAccount={selectedAdAccount}
          setSelectedAccount={setSelectedAdAccount}
          setIsStepValid={setIsStepValid}
        />
      )}
      {currentStep === 6 && (
        <CreatePixel AdAccountId={selectedAdAccount} setIsStepValid={setIsStepValid} />
      )}

      <FooterNavigation
        currentStep={currentStep}
        totalSteps={totalSteps}
        handleNext={handleNext}
        handleBack={handleBack}
        handleSubmit={handleSubmit}
        connected={isFacebookConnected}
        type="accountSetup"
        isStepValid={isStepValid}
      />
    </>
  );
}
